/**
 * The L1 Copilot adoption section on `/ai/overview`: org-level Microsoft 365
 * Copilot counts — who is licensed (enabled), who actually used it, trending
 * which way, and in which apps.
 *
 * ## A different upstream, so its own access states
 *
 * The rest of this board reads Azure ARM; this section reads the Microsoft
 * Graph **beta** report functions, app-only, with `Reports.Read.All`. Its
 * `access_state` vocabulary is therefore the Graph one (`consent_missing`,
 * `not_provisioned`, `request_rejected` — the Defender board's vocabulary),
 * and `COPILOT_ACCESS_COPY` is typed `Record<CopilotAccessState, …>` off the
 * generated union so the *compiler* keeps it exhaustive: a seventh state added
 * in `nova/copilot/models.py` fails the build here rather than rendering as a
 * blank card. When the state is not `ok` the panels do not render at all — a
 * refused read shown as `0 enabled users` would report a tenant that never
 * bought Copilot, which is wrong in the flattering direction (§5).
 *
 * ## Privacy is structural, and the section says so
 *
 * The payload contains tenant-wide counts and dates and nothing else — the
 * backend can only build the two org-count report URLs, and its route takes no
 * parameters. There is deliberately no drill-down to people here and never
 * will be: the L2 for "who" does not exist by design.
 *
 * ## Null vs zero, again
 *
 * `enabled_users`/`active_users` are nullable throughout: `null` is a cell
 * that would not parse (the beta shape is documented, not live-verified),
 * rendered as an em dash — while `0` active users in an app is a *measured*
 * finding: licences bought, nothing used.
 */

import { Chart } from "@norican/dataviz";
import { Bug, CircleCheck, CloudOff, Icon, KeyRound, PackageX, ShieldOff } from "@norican/icons";
import {
  Alert,
  Badge,
  Card,
  DescriptionList,
  EmptyState,
  Skeleton,
  Statistic,
  Typography,
} from "@norican/react";

import { ChartCard } from "@/components/board";
import type { CopilotAppAdoption, CopilotOverview, CopilotTrendPoint } from "@/lib/api";
import { useCopilotOverview } from "@/lib/api/hooks/copilot";

import { AiErrorsAlert } from "./access-panel";
import { PanelCard, SectionHeading } from "./panel-card";
import { COPILOT_ANCHOR, TWIN_IDS } from "./params";
import {
  type AccessStateCopy,
  type FigureTone,
  formatCount,
  formatPercent,
  NOT_ZERO_NOTE,
  staleAfterMs,
  tooltipValue,
} from "./status";

/** How many apps the L1 bar chart ranks before folding the tail (§1). */
const APP_LIMIT = 10;

/** The section's access states, projected out of the generated payload. */
type CopilotAccessState = CopilotOverview["access_state"];

/**
 * What each Graph access state means, whose problem it is, and what to do.
 *
 * The expected state is `ok`: `Reports.Read.All` is consented on both the app
 * registration and the managed identity. The failure states are still written
 * out in full, because "consent was revoked" and "Microsoft retired the beta
 * function" want opposite responses.
 */
const COPILOT_ACCESS_COPY: Record<CopilotAccessState, AccessStateCopy> = {
  ok: {
    title: "Reading live from Microsoft Graph",
    tone: "success",
    icon: CircleCheck,
    owner: "Nothing to do — every figure below is a tenant-wide count measured by Microsoft.",
    remedy: [],
    badge: "Live",
  },
  no_credential: {
    title: "NOVA has no Microsoft Graph credential",
    tone: "warning",
    icon: KeyRound,
    owner:
      "A NOVA deployment gap. Nothing was asked of Graph at all, so nothing is known about Copilot adoption either way — this is not a tenant with zero licences.",
    remedy: [
      "In Azure — assign the managed identity to the nova-api Container App; Reports.Read.All is already consented on it.",
      "Anywhere else — set AZURE_TENANT_ID, AZURE_CLIENT_ID and AZURE_CLIENT_SECRET for the app's own service principal.",
      "An `az login` on the host is deliberately not used: NOVA never authenticates as a person.",
    ],
    badge: "No credential",
  },
  consent_missing: {
    title: "Microsoft Graph refused the read — the token lacks Reports.Read.All",
    tone: "warning",
    icon: ShieldOff,
    owner:
      "A human has to (re-)grant an application permission. NOVA authenticated fine and asked correctly; the token does not carry the role.",
    remedy: [
      "Reports.Read.All was consented on both the app registration and the managed identity on 2026-08-20 — check whether it was revoked, and re-grant it in both places.",
      "A fresh grant can take up to ~24 h to reach a managed-identity token; the section lights up on its own once it does.",
      "Some tenants also restrict report reads to named apps via the reports authorization policy — a different setting from consent.",
    ],
    badge: "403 consent missing",
  },
  not_provisioned: {
    title: "The Copilot report functions are not served here any more",
    tone: "warning",
    icon: PackageX,
    owner:
      "A fact about the Graph API surface, not a permission problem and not an empty tenant. These functions are beta-only and Graph answered 404.",
    remedy: [
      "Granting a permission would not help — there is nothing at this address to read.",
      "If it persists, Microsoft has moved or retired the beta function and NOVA needs a code change: raise a ticket against NOVA.",
    ],
    badge: "404 not provisioned",
  },
  request_rejected: {
    title: "Microsoft Graph rejected NOVA's request",
    tone: "danger",
    icon: Bug,
    owner:
      "A NOVA-side bug. The credential is fine; the period or format NOVA sends is no longer accepted by the report function.",
    remedy: [
      "Raise a ticket against NOVA. There is nothing for an administrator to configure, grant or license here.",
      "Nothing about the numbers this section would have shown can be inferred from this state — they were never computed.",
    ],
    badge: "400 rejected by Graph",
  },
  unavailable: {
    title: "Microsoft Graph did not answer",
    tone: "warning",
    icon: CloudOff,
    owner:
      "Upstream, or an identity fault. Either Graph returned 5xx / did not complete, or it refused NOVA's token twice — once more after a fresh one was minted.",
    remedy: [
      "Transient by nature: the next poll may well succeed and the section recovers on its own.",
      "If it persists, check the Microsoft 365 service health for Microsoft Graph before touching NOVA.",
    ],
    badge: "Unavailable",
  },
};

/** Colours for the two trend series — DS chart tokens, fixed assignment (§3). */
const TREND_CONFIG = {
  enabled_users: { label: "Licensed (enabled)", color: "var(--norican-chart-2)" },
  active_users: { label: "Active", color: "var(--norican-chart-1)" },
};

/** The heading and its standing caveats, shared by every branch below. */
function CopilotHeading() {
  return (
    <SectionHeading
      id={COPILOT_ANCHOR}
      title="Copilot adoption"
      subtitle="Tenant-wide Microsoft 365 Copilot counts from Microsoft Graph — who is licensed (Microsoft's 'enabled'), who actually used it, and in which apps. Org-level aggregates only, by construction: no per-user report is ever requested, and there is deliberately no drill-down to people. The report refreshes upstream roughly daily and lags a couple of days."
    />
  );
}

export function AiCopilotSection() {
  const overview = useCopilotOverview();
  const data = overview.data;

  // A thrown error means the NOVA API is down, not Graph — every Graph outcome
  // arrives as a field on a 200.
  if (overview.error != null) {
    return (
      <section className="flex scroll-mt-6 flex-col gap-4">
        <CopilotHeading />
        <Alert tone="danger" title="Couldn't load the Copilot adoption section">
          {overview.error instanceof Error
            ? overview.error.message
            : "The NOVA API did not respond for /copilot/overview. This is a NOVA problem, not a Microsoft Graph one."}
        </Alert>
      </section>
    );
  }

  if (data == null) {
    return (
      <section className="flex scroll-mt-6 flex-col gap-4" aria-busy="true">
        <CopilotHeading />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} height="7rem" />
          ))}
        </div>
        <Skeleton height="18rem" />
      </section>
    );
  }

  return (
    <section className="flex scroll-mt-6 flex-col gap-4">
      <CopilotHeading />
      {/* errors[] sits above the figures it undermines and renders nothing
          when the list is empty. */}
      <AiErrorsAlert
        errors={data.errors}
        title="This Copilot snapshot is incomplete — the figures below are what could be read"
      />
      {data.access_state === "ok" ? (
        <CopilotPanels overview={data} />
      ) : (
        <CopilotAccessCard overview={data} />
      )}
    </section>
  );
}

interface CopilotPanelsProps {
  overview: CopilotOverview;
}

/** The `ok` branch: headline tiles, the trend, and the per-app ranking. */
function CopilotPanels({ overview }: CopilotPanelsProps) {
  const { summary, apps, trend } = overview;

  if (summary == null && apps.length === 0 && trend.length === 0) {
    return (
      <PanelCard
        title="No Copilot report yet"
        caption={`Asked for the last ${periodDays(overview)} days`}
      >
        <EmptyState
          size="sm"
          title="Microsoft Graph answered, and the report carried no rows"
          description="That is an empty report — a tenant with no Copilot usage report generated yet — not a misconfiguration and not a measured zero. A refused read would have rendered the access panel instead."
        />
      </PanelCard>
    );
  }

  const staleAfter = staleAfterMs(overview.cache_ttl_sec);
  const refreshCaption = overview.report_refresh_date
    ? `Report data as of ${overview.report_refresh_date} (Microsoft refreshes it roughly daily)`
    : "The report's own refresh date could not be read";

  /* Active-share tone: null is neutral (unknown, never green); a measured low
     share is the finding this section exists for, but the warning threshold is
     not a backend payload field here, so volume stays un-toned rather than
     judged against a number invented in the frontend. */
  const activeTone: FigureTone = summary?.active_rate_pct == null ? "neutral" : "accent";

  const rankedApps = [...apps].sort((a, b) => (b.active_users ?? -1) - (a.active_users ?? -1));
  const shownApps = rankedApps.slice(0, APP_LIMIT);

  const trendRows = trend.map((point) => ({
    ...point,
    label: formatTrendDay(point.date),
  }));
  const measuredDays = trend.filter((point) => point.active_users != null).length;

  return (
    <div className="flex flex-col gap-4">
      {/* ── Headline: licensed vs actually used ─────────────────────────── */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card variant="outline">
          <Card.Body>
            <Statistic
              label="Licensed (enabled) users"
              value={formatCount(summary?.enabled_users)}
              size="lg"
              caption={
                summary?.enabled_users == null
                  ? "The enabled-user cell could not be read — unknown, not a tenant without licences"
                  : `Users enabled for Microsoft 365 Copilot in any app, last ${periodDays(overview)} days`
              }
            />
          </Card.Body>
        </Card>
        <Card variant="outline">
          <Card.Body>
            <Statistic
              label="Active users"
              value={formatCount(summary?.active_users)}
              tone={activeTone}
              size="lg"
              caption={
                summary?.active_users == null
                  ? "The active-user cell could not be read — unknown, not zero usage"
                  : summary.active_users === 0
                    ? "A measured zero: licences assigned, Copilot used by nobody in the period"
                    : `Used Copilot in at least one app, last ${periodDays(overview)} days`
              }
            />
          </Card.Body>
        </Card>
        <Card variant="outline">
          <Card.Body>
            <Statistic
              label="Active share of licensed"
              value={formatPercent(summary?.active_rate_pct)}
              tone={activeTone}
              size="lg"
              caption={
                summary?.active_rate_pct == null
                  ? "Not computed — one side of the ratio could not be read, so no percentage is invented"
                  : "The licensed-but-idle gap is the cost finding: every unused seat still bills"
              }
            />
          </Card.Body>
        </Card>
      </div>

      {/* ── Trend: two counts over the period ───────────────────────────── */}
      <ChartCard<CopilotTrendPoint & { label: string }>
        title="Adoption over the period"
        caption={
          trendRows.length === 0
            ? "The trend report carried no days"
            : `${formatCount(measuredDays)} of ${formatCount(trendRows.length)} days measured · ${refreshCaption}`
        }
        since={overview.collected_at}
        staleAfterMs={staleAfter}
        tableTwin={{
          id: TWIN_IDS.copilotTrend,
          ariaLabel: "Licensed and active Copilot users, per day",
          data: trendRows,
          getRowId: (row) => row.date,
          columns: [
            { id: "date", header: "Day (UTC)", accessor: (row) => row.date },
            {
              id: "enabled",
              header: "Licensed (enabled)",
              align: "end",
              accessor: (row) => row.enabled_users,
              cell: (row) => <span className="tabular-nums">{formatCount(row.enabled_users)}</span>,
            },
            {
              id: "active",
              header: "Active",
              align: "end",
              accessor: (row) => row.active_users,
              cell: (row) => <span className="tabular-nums">{formatCount(row.active_users)}</span>,
            },
          ],
        }}
      >
        {trendRows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No trend days were returned"
            description="The trend report answered without daily rows, so no line is drawn — a flat line at zero would claim a quiet period nobody measured."
          />
        ) : (
          <Chart.Container
            config={TREND_CONFIG}
            className="h-64"
            aria-label={`Licensed and active Microsoft 365 Copilot users per day over the last ${periodDays(overview)} days`}
          >
            <Chart.LineChart data={trendRows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
              <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
              <Chart.XAxis {...Chart.axisProps} dataKey="label" />
              <Chart.YAxis {...Chart.axisProps} width={56} allowDecimals={false} />
              <Chart.Tooltip
                content={
                  <Chart.TooltipContent
                    // `tooltipValue` first, never `Number(value)`: a day whose
                    // cell would not parse arrives as `undefined`, and
                    // `Number(null)` would render a fabricated 0.
                    formatter={(value) => formatCount(tooltipValue(value))}
                  />
                }
              />
              <Chart.Legend content={<Chart.LegendContent />} />
              <Chart.Line
                {...Chart.seriesDefaults}
                dataKey="enabled_users"
                type="monotone"
                stroke="var(--color-enabled_users)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
              <Chart.Line
                {...Chart.seriesDefaults}
                dataKey="active_users"
                type="monotone"
                stroke="var(--color-active_users)"
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            </Chart.LineChart>
          </Chart.Container>
        )}
      </ChartCard>

      {/* ── Per-app: where the activity actually is ─────────────────────── */}
      <ChartCard<CopilotAppAdoption>
        title="Active users by app"
        caption={
          rankedApps.length === 0
            ? "The report carried no per-app rows"
            : rankedApps.length > APP_LIMIT
              ? `Top ${formatCount(shownApps.length)} of ${formatCount(rankedApps.length)} apps — the rest are in the data view`
              : `${formatCount(rankedApps.length)} app(s), discovered from the report's own fields, not enumerated`
        }
        since={overview.collected_at}
        staleAfterMs={staleAfter}
        tableTwin={{
          id: TWIN_IDS.copilotApps,
          ariaLabel: "Licensed and active Copilot users per app",
          data: rankedApps,
          getRowId: (row) => row.app,
          columns: [
            { id: "app", header: "App", accessor: (row) => row.app },
            {
              id: "enabled",
              header: "Licensed (enabled)",
              align: "end",
              accessor: (row) => row.enabled_users,
              cell: (row) => <span className="tabular-nums">{formatCount(row.enabled_users)}</span>,
            },
            {
              id: "active",
              header: "Active",
              align: "end",
              accessor: (row) => row.active_users,
              cell: (row) => <span className="tabular-nums">{formatCount(row.active_users)}</span>,
            },
          ],
        }}
      >
        {shownApps.length === 0 ? (
          <EmptyState
            size="sm"
            title="No per-app rows were returned"
            description="The summary report answered without app fields, so nothing is ranked here. A refused read would have rendered the access panel instead."
          />
        ) : (
          <Chart.Container
            config={{ active_users: { label: "Active users", color: "var(--norican-chart-1)" } }}
            className="h-64"
            aria-label={`Active Microsoft 365 Copilot users per app, top ${formatCount(shownApps.length)} of ${formatCount(rankedApps.length)}`}
          >
            <Chart.BarChart
              data={shownApps}
              layout="vertical"
              margin={{ top: 8, right: 12, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              <Chart.XAxis {...Chart.axisProps} type="number" allowDecimals={false} />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="app" width={110} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={
                  <Chart.TooltipContent
                    rows={(datum: CopilotAppAdoption) => [
                      { label: "Active users", value: formatCount(datum.active_users) },
                      { label: "Licensed (enabled)", value: formatCount(datum.enabled_users) },
                    ]}
                  />
                }
              />
              <Chart.Bar
                {...Chart.seriesDefaults}
                dataKey="active_users"
                fill="var(--color-active_users)"
                radius={[0, 3, 3, 0]}
              />
            </Chart.BarChart>
          </Chart.Container>
        )}
      </ChartCard>

      <Typography variant="body-sm" tone="muted">
        An app at 0 active users is a measured finding — licences assigned, that Copilot surface
        used by nobody in the period — while an em dash is a cell that could not be read. The two
        never render the same. There is no per-person view behind these numbers anywhere in NOVA, by
        design: the backend can only ask Microsoft for tenant-wide counts.
      </Typography>
    </div>
  );
}

interface CopilotAccessCardProps {
  overview: CopilotOverview;
}

/** What renders instead of the panels when the payload carries no data. */
function CopilotAccessCard({ overview }: CopilotAccessCardProps) {
  const copy = COPILOT_ACCESS_COPY[overview.access_state];

  const facts = [
    { label: "Section", value: "Copilot adoption" },
    { label: "Period it would cover", value: `Last ${periodDays(overview)} days` },
    {
      label: "Credential",
      value: overview.configured
        ? "Present — NOVA authenticated successfully"
        : "Absent — nothing was asked of Microsoft Graph",
    },
    {
      label: "Permissions proved missing",
      value:
        overview.missing_permissions.length > 0
          ? overview.missing_permissions.join(", ")
          : "None proved missing — and none proved granted",
    },
  ];

  return (
    <Card variant="outline" accent="left" accentTone={copy.tone}>
      <Card.Header>
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="flex items-center gap-3">
            <Icon icon={copy.icon} size="md" />
            <Typography variant="heading-4" as="h2">
              {copy.title}
            </Typography>
          </div>
          {/* State is never colour-alone (§3.2) — the badge names it in words. */}
          <Badge tone={copy.tone} variant="subtle" size="sm">
            {copy.badge}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <div className="flex flex-col gap-4">
          {/* The backend's own one-liner, verbatim — it names the permission
              and the path, which are the strings an operator has to paste. */}
          <Alert tone={copy.tone} title='Microsoft Graph: what happened to "Copilot adoption"'>
            {overview.status_detail}
          </Alert>

          <Typography variant="body-sm" tone="muted">
            {copy.owner}
          </Typography>

          {copy.remedy.length > 0 && (
            <ul className="text-fg-muted flex list-disc flex-col gap-1.5 pl-5 text-sm">
              {copy.remedy.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          )}

          <DescriptionList items={facts} columns={2} />

          <Typography variant="body-sm" tone="muted">
            {NOT_ZERO_NOTE}
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
}

/** The period, preferring the report's own claim over the requested constant. */
function periodDays(overview: CopilotOverview): string {
  if (overview.report_period_days != null) {
    return formatCount(overview.report_period_days);
  }
  return overview.period.replace(/^D/, "");
}

/** A short axis label for a `YYYY-MM-DD` report day; verbatim when unparsable. */
function formatTrendDay(day: string): string {
  const parsed = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(parsed)) {
    return day;
  }
  return new Date(parsed).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}
